import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { PHRASE_SETTING } from './phraseSetting';
import { anchoredOffCentre, check, growsLonger } from '../skillUsage';

export const underlineDrawSkill: CodeoSkill = {
    name: SKILL_NAMES.underlineDraw,
    kind: 'codeo',
    description:
        'Draw a solid rule under the last word of a phrase the moment it lands, growing from ' +
        "the word's left edge and slowing into its right, to say that this word is the point.",
    checkLooksFor: 'a rule anchored at its left edge, tweened on scaleX or width',
    documentUsesSkill: check(
        (script, markup, html) => growsLonger(script) && anchoredOffCentre(html),
    ),
    instructions: `# Underline Draw

The phrase builds word by word, and as its last word, the one that matters, lands, a **solid rule draws itself under that word**, left to right, fast at first and slowing into the word's right edge. It is the plainest emphasis there is, and the speed is what makes it feel deliberate: it covers most of the word in a few frames and spends the rest settling.

${PHRASE_SETTING}

## Markup

The rule lives inside the word it underlines, so it moves with the word. It is the word's width and about an eighth of its font size thick, sitting just under the baseline.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center"><div id="line" style="text-align: center; white-space: nowrap; line-height: 1; font-family: var(--l5-font-0), sans-serif; font-size: 130px; font-weight: 500; color: var(--l5-color-1, #f6f4f0);"><span class="w">it</span> <span class="w">gives</span> <span class="w">you</span> <span class="w">back</span> <span class="w">a</span> <span class="w key" style="font-weight: 700; position: relative;">video<i id="rule"></i></span></div></div>
\`\`\`

\`\`\`css
.w { display: inline-block; }
#rule {
  position: absolute; left: 0; right: 0; top: 1.05em; height: 0.12em;
  background: var(--l5-color-2, #7e58f4);
  transform-origin: 0% 50%;
}
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 1.4;
const KEY_CUE = 0.3;        // the underlined word's cue

gsap.set('#rule', { scaleX: 0 });
tl.to('#rule', { scaleX: 1, duration: 0.5, ease: 'power2.out' }, KEY_CUE - 0.04);

tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Colour.** The rule takes the gradient's violet end on a dark ground, or the ink on a light one. Never the same colour as a gradient-filled word over it: it disappears into the letters.
- **With the word's own entrance.** The rule starts as the word is still settling, a frame or two before it lands, so the two read as one gesture.
- **Leaving.** The rule leaves with its line (the lift or the cut); it is never retracted on its own.

## Pitfalls

- **From the left edge.** The \`transform-origin\` is the word's left edge; a rule growing from its centre is a different, softer gesture.
- **Inside the word.** A rule positioned on the frame drifts off its word as soon as the word moves; put it inside the word's span.
- **One per graphic.** Two underlines say nothing.`,
};
